<template>
  <component :is="rootElement" v-bind="rootAttributes" class="card" :class="variantClass" @click="onClick">
    <span class="card-header">
      <h3 class="card-title">{{ title }}</h3>
      <span v-if="badge" class="badge" :class="badgeClass">{{ badge }}</span>
    </span>

    <span v-if="description" class="card-description">{{ description }}</span>

    <!-- Contenu libre : compteurs, listes, graphiques. Plusieurs cards ont un corps
         que les props ne couvrent pas. -->
    <slot />

    <span v-if="linkLabel" class="card-link">{{ linkLabel }} →</span>
  </component>
</template>

<script>
// Variantes de badge admises. La sémantique de chaque couleur est documentée dans
// docs/design-system-dashboards.md : une couleur ne s'y choisit pas à l'esthétique.
const BADGE_VARIANTS = ["blue", "green", "orange", "yellow", "red", "purple", "grey"]

// Variantes de mise en avant de la card elle-même.
const CARD_VARIANTS = ["default", "featured", "highlight"]

/**
 * Card de dashboard Labricot.
 *
 * Source unique du motif que les six dashboards recopiaient : le markup et le style
 * de card ne leur appartiennent plus, donc ils ne peuvent plus diverger.
 *
 * RGAA : l'élément racine est choisi selon l'usage — un lien quand une destination
 * est fournie, un bouton sinon. Un appelant ne peut pas produire une card
 * inaccessible, ce qui était le défaut du motif `<div class="card" @click>` qu'elle
 * remplace : un `div` n'est ni atteignable ni activable au clavier.
 */
export default {
  name: "DashboardCard",

  props: {
    // Titre de la card, emoji de tête compris — la convention veut que le même
    // emoji soit repris dans le titre de la modale ouverte.
    title: {
      type: String,
      required: true,
    },
    // Une phrase décrivant le contenu de la card, pas l'action qu'elle déclenche.
    description: {
      type: String,
      default: "",
    },
    // Verbe à l'infinitif ; la flèche est ajoutée par le composant.
    linkLabel: {
      type: String,
      default: "",
    },
    // État rendu par un texte, jamais par la seule couleur (RGAA).
    badge: {
      type: String,
      default: "",
    },
    badgeVariant: {
      type: String,
      default: "blue",
      validator: (value) => BADGE_VARIANTS.includes(value),
    },
    variant: {
      type: String,
      default: "default",
      validator: (value) => CARD_VARIANTS.includes(value),
    },
    // Destination interne. Fournie, la card devient un lien de navigation.
    to: {
      type: [String, Object],
      default: null,
    },
    // Destination externe ou téléchargement (cards d'export). Rend un <a>.
    href: {
      type: String,
      default: "",
    },
    download: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    /**
     * Le rôle de la card détermine son élément HTML, pas l'inverse :
     * naviguer est un lien, déclencher est un bouton.
     */
    rootElement() {
      if (this.to) return "router-link"
      if (this.href) return "a"
      return "button"
    },

    rootAttributes() {
      if (this.to) return { to: this.to }
      if (this.href) return this.download ? { href: this.href, download: "" } : { href: this.href }
      // Sans `type`, un bouton dans un formulaire vaut `submit`.
      return { type: "button" }
    },

    badgeClass() {
      return `badge-${this.badgeVariant}`
    },

    variantClass() {
      return this.variant === "default" ? null : `card-${this.variant}`
    },
  },

  methods: {
    onClick(event) {
      this.$emit("click", event)
    },
  },
}
</script>

<style scoped lang="scss">
// Socle partagé — ne pas redéclarer ces règles ici ni ailleurs.
@import "../scss/dashboard-card";

// La card est posée dans une `.cards-grid` : elle doit occuper toute la hauteur de
// sa cellule pour s'aligner sur ses voisines.
.card {
  height: 100%;
}

// Les enfants sont des `span` — ni un lien ni un bouton ne peut contenir un bloc —
// donc leur disposition est rétablie ici.
.card-description {
  display: block;
}

.card-header {
  gap: 0.5rem;
}

.card:hover .card-link {
  color: #1d4ed8;
}
</style>
